import React, { useCallback, useEffect, useRef, useState } from "react";
import ImageTransformations from "../zone-layout-preview/image-transformations";
import { ImagePoint } from "../../../data/image-point";
import { ZONE_LAYOUT_ACTIVE_ASPECT_RATIO } from "../inventory-vars.icss.scss";
import whitelabel from "../../../whitelabel";

export interface PolygonDrawingLayerProps {
	className?: string;

	// Points to render in normalized coordinates (0.0 to 1.0) within active area
	points: ImagePoint[];
	onChangePoints: React.Dispatch<React.SetStateAction<ImagePoint[]>>;

	// Whether points can be edited
	editable?: boolean;

	// Callback when polygon is completed
	onPolygonComplete?: (points: ImagePoint[]) => void;

	// Viewport transformations to apply to the drawing area
	transformations: ImageTransformations;
}

const DOUBLE_CLICK_DELAY = 300;
const CLICK_MOVE_THRESHOLD = 5;

const COLOR_LINES = whitelabel.palette.primary900;
const COLOR_FILL = whitelabel.palette.primary900;
const COLOR_FILL_OPACITY = 0.3;
const COLOR_POINTS = whitelabel.palette.secondary50;

/**
 * A layer that allows users to draw polygons by clicking to place points.
 * - Single click: Add a point (only within the active area)
 * - Double click: Complete the polygon
 * - Shows blue circles at each point
 * - Shows blue lines connecting points
 * - Shows semi-transparent blue fill for the polygon area
 * - Points are stored as normalized coordinates (0.0 to 1.0) within the active area
 */
export default function PolygonDrawingLayer(props: PolygonDrawingLayerProps) {
	const editable = props.editable ?? true;

	//A ref to the canvas element
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	//The size of the canvas element
	const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

	//The position of the mouse cursor within the active area in normalized coordinates
	const [normalizedMousePosition, setNormalizedMousePosition] =
		useState<ImagePoint | null>(null);

	//Mouse tracking
	const lastClickTime = useRef<number>(0);
	const mouseDownPosition = useRef<{ x: number; y: number } | null>(null);
	const hasDragged = useRef<boolean>(false);

	/// Gets the active area bounds relative to the canvas in px
	const getActiveAreaBounds = useCallback(() => {
		//Resolve the size of the internal canvas
		const canvas = canvasRef.current!;
		const canvasWidth = canvas.clientWidth;
		const canvasHeight = canvas.clientHeight;

		//Resolve the active area within the canvas
		const activeAreaScale = Math.min(
			canvasWidth / ZONE_LAYOUT_ACTIVE_ASPECT_RATIO,
			canvasHeight * ZONE_LAYOUT_ACTIVE_ASPECT_RATIO,
		);
		const activeAreaWidth =
			activeAreaScale * ZONE_LAYOUT_ACTIVE_ASPECT_RATIO;
		const activeAreaHeight =
			activeAreaScale / ZONE_LAYOUT_ACTIVE_ASPECT_RATIO;
		const activeAreaX = (canvasWidth - activeAreaWidth) / 2;
		const activeAreaY = (canvasHeight - activeAreaHeight) / 2;

		return {
			x: activeAreaX,
			y: activeAreaY,
			width: activeAreaWidth,
			height: activeAreaHeight,
		};
	}, []);

	/// Converts canvas coordinates to normalized coordinates (0.0 to 1.0) within active area
	const canvasToNormalized = useCallback(
		(canvasX: number, canvasY: number): ImagePoint | null => {
			const area = getActiveAreaBounds();

			//Transform from screen space to internal canvas space
			const transformedX =
				(canvasX - props.transformations.panX) /
				props.transformations.zoom;
			const transformedY =
				(canvasY - props.transformations.panY) /
				props.transformations.zoom;

			//Check if point is within active area
			if (
				transformedX < area.x ||
				transformedX > area.x + area.width ||
				transformedY < area.y ||
				transformedY > area.y + area.height
			) {
				// Point is outside active area
				return null;
			}

			//Transform from internal canvas space to normalized space within the active area
			const normalizedX = (transformedX - area.x) / area.width;
			const normalizedY = (transformedY - area.y) / area.height;

			return { x: normalizedX, y: normalizedY };
		},
		[props.transformations, getActiveAreaBounds],
	);

	// Convert normalized coordinates to transformed canvas coordinates for rendering
	const normalizedToCanvas = useCallback(
		(point: ImagePoint): ImagePoint => {
			//Get the active area
			const area = getActiveAreaBounds();

			//Convert from normalized to active area coordinates
			const x = area.x + point.x * area.width;
			const y = area.y + point.y * area.height;

			//Apply the transformations
			return props.transformations.transformPoint({ x, y });
		},
		[props.transformations, getActiveAreaBounds],
	);

	//Handle mouse down to track the start of a click or potential drag
	const handleMouseDown = useCallback(
		(event: React.MouseEvent<HTMLCanvasElement>) => {
			mouseDownPosition.current = {
				x: event.clientX,
				y: event.clientY,
			};
			hasDragged.current = false;
		},
		[],
	);

	//Handle mouse move to track cursor position and detect dragging
	const handleMouseMove = useCallback(
		(event: React.MouseEvent<HTMLCanvasElement>) => {
			//Check if the user is dragging
			if (mouseDownPosition.current !== null && !hasDragged.current) {
				//Resolve how far the user has moved their mouse
				const dx = event.clientX - mouseDownPosition.current.x;
				const dy = event.clientY - mouseDownPosition.current.y;
				const distance = Math.sqrt(dx * dx + dy * dy);

				//If they've moved their mouse too far, consider this a drag rather than a click
				if (distance > CLICK_MOVE_THRESHOLD) {
					hasDragged.current = true;
				}
			}

			//Convert the point to normalized coordinates
			const canvasRect = event.currentTarget.getBoundingClientRect();
			const canvasX = event.clientX - canvasRect.left;
			const canvasY = event.clientY - canvasRect.top;
			const normalizedPoint = canvasToNormalized(canvasX, canvasY);
			setNormalizedMousePosition(normalizedPoint);
		},
		[canvasToNormalized, setNormalizedMousePosition],
	);

	// Handle mouse leave to clear cursor tracking
	const handleMouseLeave = useCallback(() => {
		setNormalizedMousePosition(null);
	}, [setNormalizedMousePosition]);

	// Handle click to add points or complete polygon
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLCanvasElement>) => {
			const isDrag = hasDragged.current;

			//Reset drag tracking
			mouseDownPosition.current = null;
			hasDragged.current = false;

			//If the user is dragging, ignore this click
			if (isDrag) {
				return;
			}

			const canvas = event.currentTarget;
			const rect = canvas.getBoundingClientRect();

			//Get the click position relative to the canvas element
			const x = event.clientX - rect.left;
			const y = event.clientY - rect.top;

			//Make sure the click is inside the active area
			const normalizedPoint = canvasToNormalized(x, y);
			if (normalizedPoint === null) {
				return;
			}

			const now = Date.now();
			const timeSinceLastClick = now - lastClickTime.current;
			lastClickTime.current = now;

			// Double-click detection
			if (
				timeSinceLastClick < DOUBLE_CLICK_DELAY &&
				props.points.length >= 3
			) {
				//Complete the polygon
				props.onPolygonComplete?.(props.points);
				setNormalizedMousePosition(null);
			} else {
				//Add a new point
				props.onChangePoints((prev) => [...prev, normalizedPoint]);
			}
		},
		[props.onChangePoints, props.onPolygonComplete, canvasToNormalized],
	);

	// Draw the polygon on the canvas
	useEffect(() => {
		const canvas = canvasRef.current!;
		const context = canvas.getContext("2d")!;

		// Get canvas dimensions
		const canvasWidth = canvas.clientWidth * devicePixelRatio;
		const canvasHeight = canvas.clientHeight * devicePixelRatio;

		// Update canvas internal dimensions
		canvas.width = canvasWidth;
		canvas.height = canvasHeight;

		// Clear the canvas
		context.reset();
		context.clearRect(0, 0, canvasWidth, canvasHeight);

		//Apply high-DPI scaling
		context.scale(devicePixelRatio, devicePixelRatio);

		// If there are no points, nothing to draw
		if (props.points.length === 0) {
			return;
		}

		//Collect points to render, including the mouse pointer
		let effectivePoints = props.points;
		if (normalizedMousePosition !== null && editable) {
			effectivePoints = [...props.points, normalizedMousePosition];
		}

		// Convert normalized points to canvas coordinates
		const canvasPoints = effectivePoints.map((point) =>
			normalizedToCanvas(point),
		);

		// Draw the polygon fill and stroke
		if (canvasPoints.length >= 2) {
			context.beginPath();
			context.moveTo(canvasPoints[0].x, canvasPoints[0].y);

			for (let i = 1; i < canvasPoints.length; i++) {
				context.lineTo(canvasPoints[i].x, canvasPoints[i].y);
			}

			// Close the path and fill if we have enough points
			if (canvasPoints.length >= 3) {
				context.closePath();
				context.fillStyle = COLOR_FILL;
				context.globalAlpha = COLOR_FILL_OPACITY;
				context.fill();
				context.globalAlpha = 1;
			}

			//Draw the lines
			context.strokeStyle = COLOR_LINES;
			context.lineWidth = 2;
			context.stroke();
		}

		// Draw circles at each point
		for (const point of canvasPoints) {
			context.beginPath();
			context.arc(point.x, point.y, 4, 0, 2 * Math.PI);
			context.fillStyle = COLOR_POINTS;
			context.fill();
			context.strokeStyle = COLOR_LINES;
			context.lineWidth = 2;
			context.stroke();
		}
	}, [
		props.points,
		editable,
		normalizedMousePosition,
		props.transformations,
		normalizedToCanvas,
		canvasToNormalized,
		canvasSize,
	]);

	// Update canvas size when container resizes
	useEffect(() => {
		const canvas = canvasRef.current!;

		const updateCanvasSize = () => {
			const rect = canvas.getBoundingClientRect();
			setCanvasSize({ width: rect.width, height: rect.height });
		};

		updateCanvasSize();

		// Use ResizeObserver to handle container size changes
		const resizeObserver = new ResizeObserver(updateCanvasSize);
		resizeObserver.observe(canvas);

		return () => {
			resizeObserver.disconnect();
		};
	}, [setCanvasSize]);

	return (
		<canvas
			ref={canvasRef}
			className={props.className}
			style={{
				cursor:
					editable && normalizedMousePosition !== null
						? "crosshair"
						: "default",
			}}
			onMouseDown={editable ? handleMouseDown : undefined}
			onMouseMove={editable ? handleMouseMove : undefined}
			onMouseLeave={editable ? handleMouseLeave : undefined}
			onClick={editable ? handleClick : undefined}
		/>
	);
}
